import React, { useMemo, useState } from 'react'
import { Download, History } from 'lucide-react'
import type { Permission, PermissionBundle, Role } from '@/types'
import { usePermissions } from '@/hooks/usePermissions'
import { useAllPermissions, useUpdateRolePermissions } from '../hooks/useRoles'
import { SectionLoader } from '@/components/common/SectionLoader'
import { cn } from '@/lib/cn'
import { QueryErrorState } from '@/components/common/QueryErrorState'

const cardClasses =
  'rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800'

function roleHasPermission(role: Role, permission: Permission): boolean {
  return role.permissions.some(
    (b) => b.module === permission.module && b.actions.includes(permission.action)
  )
}

function withPermissionToggled(
  bundles: PermissionBundle[],
  permission: Permission,
  granted: boolean
): PermissionBundle[] {
  if (granted) {
    const existing = bundles.find((b) => b.module === permission.module)
    if (existing) {
      if (existing.actions.includes(permission.action)) return bundles
      return bundles.map((b) =>
        b.module === permission.module
          ? { ...b, actions: [...b.actions, permission.action] }
          : b
      )
    }
    return [...bundles, { module: permission.module, actions: [permission.action] }]
  }
  return bundles
    .map((b) => {
      if (b.module !== permission.module) return b
      const actions = b.actions.filter((a) => a !== permission.action)
      return actions.length ? { ...b, actions } : null
    })
    .filter((b): b is PermissionBundle => b !== null)
}

interface PermissionsMatrixProps {
  roles: Role[]
  isLoading: boolean
  error: Error | null
  /** Re-runs the query behind this list from the error state's retry button. */
  onRetry?: () => Promise<unknown> | void
}

export function PermissionsMatrix({
  roles,
  isLoading,
  error,
  onRetry,
}: PermissionsMatrixProps): React.ReactElement {
  const { data: allPermissions = [], isLoading: permissionsLoading } = useAllPermissions()
  const updatePermissions = useUpdateRolePermissions()
  const { can: canPermission, isLoading: permissionsUserLoading } = usePermissions()
  const canUpdateRolePermissions = canPermission('roles', 'update')
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(
    roles[0]?._id ?? null
  )

  const permissionLabel = (p: Permission): string => {
    if (p.code) return p.code.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    return `${p.module} ${p.action}`.replace(/\b\w/g, (c) => c.toUpperCase())
  }

  const matrixPermissions = useMemo<Permission[]>(() => {
    const byKey = new Map<string, Permission>()

    for (const permission of allPermissions) {
      byKey.set(`${permission.module}:${permission.action}`, permission)
    }

    for (const role of roles) {
      for (const bundle of role.permissions) {
        for (const action of bundle.actions) {
          const key = `${bundle.module}:${action}`
          if (byKey.has(key)) continue

          byKey.set(key, {
            _id: `derived:${key}`,
            code: `${bundle.module}:${action}`,
            module: bundle.module,
            action,
            category: bundle.module,
            description: `${action.toUpperCase()} in ${bundle.module}`,
          })
        }
      }
    }

    return Array.from(byKey.values())
  }, [allPermissions, roles])

  const grouped = useMemo(() => {
    const byCategory = new Map<string, Permission[]>()
    for (const p of matrixPermissions) {
      const key = (p.category ?? p.module ?? 'Other').toUpperCase()
      if (!byCategory.has(key)) byCategory.set(key, [])
      byCategory.get(key)!.push(p)
    }
    const order = ['ATTENDANCE', 'FINANCIALS', 'ADMINISTRATIVE', 'OTHER']
    const sorted = Array.from(byCategory.entries()).sort(([a], [b]) => {
      const ia = order.findIndex((o) => a.includes(o))
      const ib = order.findIndex((o) => b.includes(o))
      if (ia !== -1 && ib !== -1) return ia - ib
      if (ia !== -1) return -1
      if (ib !== -1) return 1
      return a.localeCompare(b)
    })
    return sorted.map(([category, permissions]) => [
      category,
      [...permissions].sort((a, b) => permissionLabel(a).localeCompare(permissionLabel(b))),
    ] as [string, Permission[]])
  }, [matrixPermissions])

  const handleToggle = (roleId: string, permission: Permission, granted: boolean): void => {
    if (!canUpdateRolePermissions) return
    const role = roles.find((r) => r._id === roleId)
    if (!role) return
    const next = withPermissionToggled(role.permissions, permission, granted)
    updatePermissions.mutate({ roleId, payload: { permissions: next } })
  }

  if (isLoading || permissionsLoading || permissionsUserLoading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <SectionLoader variant="table" cols={4} rows={6} label="Loading permissions matrix" />
      </div>
    )
  }

  if (error) {
    return (
      <QueryErrorState error={error} title="Couldn't load the permissions matrix" onRetry={onRetry} />
    )
  }

  if (roles.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white py-12 text-center text-sm text-gray-600 shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
        No roles yet. Create a role to configure the permissions matrix.
      </div>
    )
  }

  return (
    <div className={cardClasses}>
      <div className="p-4 sm:p-6">
        <div className="mb-6 flex flex-wrap gap-2">
          {roles.map((role) => (
            <button
              key={role._id}
              type="button"
              onClick={() => setSelectedRoleId(role._id)}
              className={cn(
                'rounded-lg px-4 py-2.5 text-sm font-medium transition-colors',
                selectedRoleId === role._id
                  ? 'bg-primary text-white shadow-sm'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600'
              )}
            >
              <span className="block">{role.name}</span>
              <span className="mt-0.5 block text-xs font-normal opacity-90">
                {role.isDefault ? 'Default' : 'Custom'}
              </span>
            </button>
          ))}
        </div>

        {!canUpdateRolePermissions && (
          <p className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
            You do not have permission to change role permissions (roles:update).
          </p>
        )}

        <div className="overflow-x-auto lg:overflow-visible">
          <table className="min-w-full border-separate border-spacing-0 text-left text-sm">
            <thead>
              <tr>
                <th className="pointer-events-none sticky top-14 z-20 w-[min(280px,40%)] border-b border-gray-200 bg-gray-50 px-4 py-3 font-semibold uppercase tracking-wider text-gray-600 shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
                  Permission bundle
                </th>
                {roles.map((role) => (
                  <th
                    key={role._id}
                    className={cn(
                      'sticky top-14 z-20 border-b border-gray-200 bg-white p-0 text-center font-semibold uppercase tracking-wider text-gray-600 shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400',
                      selectedRoleId === role._id && 'bg-blue-100 dark:bg-gray-700'
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => setSelectedRoleId(role._id)}
                      aria-pressed={selectedRoleId === role._id}
                      className="flex h-full min-h-12 w-full items-center justify-center px-4 py-3 text-inherit transition-colors hover:bg-blue-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/50 dark:hover:bg-gray-700"
                    >
                      {role.name}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grouped.map(([category, perms]) => (
                <React.Fragment key={category}>
                  <tr className="bg-gray-50 dark:bg-gray-800/50">
                    <td
                      colSpan={roles.length + 1}
                      className="border-b border-gray-200 px-4 py-2 text-xs font-bold uppercase tracking-wider text-gray-600 dark:border-gray-700 dark:text-gray-400"
                    >
                      {category.replace(/_/g, ' ')}
                    </td>
                  </tr>
                  {perms.map((permission) => (
                    <tr
                      key={permission._id}
                      className="border-b border-gray-100 transition-colors even:bg-gray-50/50 hover:bg-gray-50 dark:border-gray-700 dark:even:bg-gray-800/30 dark:hover:bg-gray-800/50"
                    >
                      <td className="border-b border-gray-100 px-4 py-3 dark:border-gray-700">
                        <div className="font-medium text-gray-900 dark:text-gray-100">
                          {permissionLabel(permission)}
                        </div>
                        {permission.description && (
                          <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                            {permission.description}
                          </div>
                        )}
                      </td>
                      {roles.map((role) => {
                        const granted = roleHasPermission(role, permission)
                        const isUpdating =
                          updatePermissions.isPending &&
                          updatePermissions.variables?.roleId === role._id
                        return (
                          <td
                            key={role._id}
                            className={cn(
                              'border-b border-gray-100 py-3 text-center dark:border-gray-700',
                              selectedRoleId === role._id && 'bg-blue-50 dark:bg-gray-700'
                            )}
                          >
                            <label
                              className={cn(
                                'inline-flex items-center justify-center',
                                canUpdateRolePermissions ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'
                              )}
                            >
                              <input
                                type="checkbox"
                                checked={granted}
                                disabled={isUpdating || !canUpdateRolePermissions}
                                onChange={(e) =>
                                  handleToggle(role._id, permission, e.target.checked)
                                }
                                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary dark:border-gray-600 dark:bg-gray-700"
                              />
                              <span className="sr-only">
                                {permissionLabel(permission)} for {role.name}
                              </span>
                            </label>
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 flex flex-col items-start justify-between gap-3 border-t border-gray-200 pt-4 dark:border-gray-700 sm:flex-row sm:items-center">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Changes are saved automatically to the secure vault.
          </p>
          <div className="flex items-center gap-4">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100"
            >
              <Download className="h-4 w-4" aria-hidden />
              Export Matrix
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100"
            >
              <History className="h-4 w-4" aria-hidden />
              View Revision History
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

PermissionsMatrix.displayName = 'PermissionsMatrix'
